import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getOrgProfile } from 'api/OnboardingController';

import {
  OrgLocalOnboardingProfile,
  OrgOnboardingSearchParams,
  OrgRemoteOnboardingProfile,
} from '../types';
import { LoadingView } from '../pages/common/LoadingView';
import AlreadyClaimedView from '../pages/Org/epic-3/AlreadyClaimedView';

type OrgOnboardingContextValue = {
  orgClaimHash: string;
  orgProfile: OrgLocalOnboardingProfile;
  updateLocalOrgProfile: (
    nextProfile: Partial<OrgLocalOnboardingProfile>,
  ) => void;
  selectedOrg?: string;
  setSelectedOrg: (id: string) => void;
  getSearchParam: (key: OrgOnboardingSearchParams) => string;
};

export const OrgOnboardingContext = createContext<
  undefined | OrgOnboardingContextValue
>(undefined);

export const useOrgOnboardingContext = () => {
  const onboardingContext = useContext(OrgOnboardingContext);
  //
  // wait for the local profile to be populated from localstorage before proceeding.
  //
  if (!onboardingContext) {
    return null;
  }

  return onboardingContext;
};

const createLocalStorageKey = (hash: string) => `bb.onboarding~${hash}`;

const readProfileFromLocalStorage = (
  profile: OrgRemoteOnboardingProfile,
  hash: string,
) => {
  try {
    const key = createLocalStorageKey(hash);
    const value = localStorage.getItem(key);

    return {
      phone: '',
      ...profile,
      ...JSON.parse(value),
    };
  } catch (e) {
    // fixme: do something better with errors here
    // eslint-disable-next no-console
    console.warn(e.getMessage());

    return profile;
  }
};

const saveProfileToLocalStorage = (
  profile: OrgRemoteOnboardingProfile,
  hash: string,
) => {
  const key = createLocalStorageKey(hash);
  const value = JSON.stringify(profile);

  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // fixme: do something better with errors here
    // eslint-disable-next no-console
    console.error(e.getMessage());
  }
};

type OrgOnboardingContextProviderProps = {
  hash: string;
};

export const OrgOnboardingContextProvider: React.FC<
  OrgOnboardingContextProviderProps
> = props => {
  const { hash, children } = props;
  const { search } = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  // Epic 1
  const [selectedOrg, setSelectedOrg] = useState<string>();
  const [remoteProfile, setRemoteProfile] =
    useState<OrgRemoteOnboardingProfile | null>(null);
  const [localProfile, setLocalProfile] =
    useState<OrgLocalOnboardingProfile | null>(null);

  const updateLocalOrgProfile = (nextProfile: OrgLocalOnboardingProfile) => {
    const combinedNextProfile = {
      ...localProfile,
      ...remoteProfile,
      ...nextProfile,
    };

    saveProfileToLocalStorage(combinedNextProfile, hash);
    setLocalProfile(combinedNextProfile);
  };

  useEffect(() => {
    // @TODO: handle not found case
    const fetchProfile = async () => {
      setIsFetchingProfile(true);
      const result = await getOrgProfile({ hash });

      setRemoteProfile(result);

      if (localProfile?.id !== result?.id) {
        const tempProfile = readProfileFromLocalStorage(result, hash);

        setLocalProfile(tempProfile);
      }
      setIsFetchingProfile(false);
    };

    fetchProfile();
  }, [localProfile?.id, hash]);

  if (isFetchingProfile) {
    return <LoadingView includeLayout />;
  }

  if (
    // We use remote profile here because we don't want to take our outdated
    // local profile as source of truth for this check
    remoteProfile.isClaimed ||
    (remoteProfile.status === 'ONBOARDING' &&
      remoteProfile.onboarding.currentStage?.name !== 'CLAIM')
  ) {
    return <AlreadyClaimedView />;
  }

  const getSearchParam = (key: OrgOnboardingSearchParams) =>
    searchParams.get(key);

  return (
    <OrgOnboardingContext.Provider
      value={{
        orgClaimHash: hash,
        orgProfile: localProfile,
        updateLocalOrgProfile,
        selectedOrg,
        setSelectedOrg,
        getSearchParam,
      }}
    >
      {children}
    </OrgOnboardingContext.Provider>
  );
};
